/**
 * Reiter *Stammdaten* für einen Vertrag (Spec 03, „Reiter Stammdaten eines
 * Vertrags"). Struktur und Verhalten spiegeln die drei Arten aus 02a:
 * Container besitzt die Query, diese Komponente den lokalen
 * Bearbeitungszustand und die Mutationen.
 *
 * Über den Feldern steht eine nicht bearbeitbare Zeile mit Vertragsobjekt
 * (`contract-subject`, Link), Status (`contract-status`, wörtlich
 * `status.label`) und `period_display` — alle drei kommen fertig vom Server
 * (I9), diese Komponente rechnet nichts.
 */

import type { UseQueryResult } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { ArchiveControls } from '@/components/ArchiveControls'
import { ArchivedBanner } from '@/components/ArchivedBanner'
import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { MasterDataEditSheet } from '@/components/MasterDataEditSheet'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { ApiError } from '@/lib/api'
import { centsToDecimalInput, parseCents } from '@/lib/money'
import type { ContractDetail } from '@/features/contracts/api'
import { useArchiveContract, useArchivePreview, useUnarchiveContract, useUpdateContract } from '@/features/contracts/api'
import { buildContractPatchBody } from '@/features/contracts/contract-patch'
import type { ContractFormValues } from '@/features/contracts/contract-patch'
import { ContractTermsSheet } from '@/features/contracts/ContractTermsSheet'
import { ContractTermsSummary } from '@/features/contracts/ContractTermsSummary'
import { FormField, FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { integerToInputValue, parseIntegerInput } from '@/features/contracts/integer'
import { TenantPicker } from '@/features/contracts/TenantPicker'
import type { TenantSelection } from '@/features/contracts/TenantPicker'

interface ContractMasterDataProps {
  readonly entityId: string
  readonly canEdit?: boolean
  readonly canUnarchive: boolean
  /** `ObjectHead.archived_by_id`/`archived_by_label` — kommen von
   * `routes/ObjectDetail.tsx`, nicht von `ContractDetail` (I9). */
  readonly archivedById: string | null
  readonly archivedByLabel: string | null
  readonly query: UseQueryResult<ContractDetail>
  /** Trägt der Vertrag mindestens eine Sollstellung? `ContractDetail` selbst
   * hat dafür kein Feld — abgeleitet vom Aufrufer aus derselben
   * `useContractCharges`-Abfrage, die auch der Reiter *Geld* benutzt (Spec
   * 04a, „Konditionen wechseln zum Monatsersten"). Solange die Abfrage noch
   * lädt, gilt `false`: Die Felder bleiben bedienbar, ein verspäteter 409
   * `terms_locked` beim Speichern ist der seltene Randfall, kein Normalfall. */
  readonly hasCharges: boolean
}

function deriveForm(contract: ContractDetail): ContractFormValues {
  return {
    tenantId: contract.tenant.id,
    tenantLabel: contract.tenant.label,
    startsOn: contract.starts_on,
    baseRent: centsToDecimalInput(contract.base_rent_net_cents),
    operatingCostAdvance: centsToDecimalInput(contract.operating_cost_advance_net_cents),
    paymentDueDay: integerToInputValue(contract.payment_due_day),
    deposit: contract.deposit_cents === null ? '' : centsToDecimalInput(contract.deposit_cents),
    noticePeriodMonths: contract.notice_period_months === null ? '' : integerToInputValue(contract.notice_period_months),
    endsOn: contract.ends_on ?? '',
    terminatedOn: contract.terminated_on ?? '',
    contractRef: contract.contract_ref ?? '',
  }
}

function ContractReadField({ label, value, testId }: { readonly label: string; readonly value: string | number | null; readonly testId: string }) {
  return <div><dt className="text-label text-muted">{label}</dt><dd data-testid={testId} className="text-body text-text">{value ?? '—'}</dd></div>
}

export function ContractMasterData({
  entityId,
  canEdit = false,
  canUnarchive,
  archivedById,
  archivedByLabel,
  query,
  hasCharges,
}: ContractMasterDataProps) {
  return (
    <section data-testid="contract-master-data" className="px-(--page-x) py-4">
      {query.isPending && <SkeletonRows testId="contract-master-data-loading" rows={10} />}

      {query.isError && (
        <ErrorState
          testId="contract-master-data-error"
          retryTestId="contract-master-data-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {query.isSuccess && (
        <ContractMasterDataForm
          entityId={entityId}
          canEdit={canEdit}
          canUnarchive={canUnarchive}
          archivedById={archivedById}
          archivedByLabel={archivedByLabel}
          contract={query.data}
          hasCharges={hasCharges}
        />
      )}
    </section>
  )
}

function ContractMasterDataForm({
  entityId,
  canEdit,
  canUnarchive,
  archivedById,
  archivedByLabel,
  contract,
  hasCharges,
}: {
  readonly entityId: string
  readonly canEdit: boolean
  readonly canUnarchive: boolean
  readonly archivedById: string | null
  readonly archivedByLabel: string | null
  readonly contract: ContractDetail
  readonly hasCharges: boolean
}) {
  const updateMutation = useUpdateContract()
  const archiveMutation = useArchiveContract()
  const unarchiveMutation = useUnarchiveContract()
  const archivePreviewMutation = useArchivePreview()

  const [form, setForm] = useState<ContractFormValues>(() => deriveForm(contract))
  const [initial, setInitial] = useState<ContractFormValues>(() => deriveForm(contract))
  // Nur fürs Anzeigen in `TenantPicker` (Zweitzeile) — nicht Teil des
  // Vergleichs `isDirty` und nicht Teil des Rumpfs: `ContractDetail.tenant`
  // (`ContractParty`) trägt keine Anrede, die kommt erst zurück, sobald der
  // Nutzer über die Suche neu wählt (Spec 03, „Antwort GET /contracts": die
  // Anrede fehlt in `ContractParty` bewusst).
  const [tenantSalutation, setTenantSalutation] = useState<TenantSelection['salutation']>(null)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)
  const [archiveError, setArchiveError] = useState<string | undefined>(undefined)
  const [isTermsSheetOpen, setIsTermsSheetOpen] = useState(false)
  const editTriggerRef = useRef<HTMLButtonElement>(null)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [confirmation, setConfirmation] = useState<string | null>(null)

  const [syncedKey, setSyncedKey] = useState(`${contract.id}:${String(contract.version)}`)
  const currentKey = `${contract.id}:${String(contract.version)}`
  if (currentKey !== syncedKey) {
    setSyncedKey(currentKey)
    const next = deriveForm(contract)
    setForm(next)
    setInitial(next)
    setTenantSalutation(null)
  }

  const isArchived = contract.archived_at !== null
  const isDirty = JSON.stringify(form) !== JSON.stringify(initial)

  // Drei Felder sind Pflicht und ohne Vorgabewert im Rumpf (Spec 03, „Namen,
  // leere Zeichenketten, Felder leeren": Miete, Nebenkostenvorauszahlung und
  // Fälligkeitstag sind „nicht leerbar") — ein leeres oder ungültiges Feld
  // deaktiviert „Speichern", statt still einen Ersatzwert zu senden.
  const parsedBaseRent = parseCents(form.baseRent)
  const parsedOperatingCostAdvance = parseCents(form.operatingCostAdvance)
  const parsedPaymentDueDay = parseIntegerInput(form.paymentDueDay)
  const isValid =
    form.startsOn !== '' &&
    form.tenantId !== '' &&
    parsedBaseRent !== null &&
    parsedOperatingCostAdvance !== null &&
    parsedPaymentDueDay !== null

  function updateField(name: keyof ContractFormValues) {
    return (value: string) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  function selectTenant(tenant: TenantSelection) {
    setForm((current) => ({ ...current, tenantId: tenant.id, tenantLabel: tenant.label }))
    setTenantSalutation(tenant.salutation)
  }

  function openEditor() {
    const next = deriveForm(contract)
    setForm(next)
    setInitial(next)
    setTenantSalutation(null)
    setFieldError(undefined)
    setGeneralError(undefined)
    setIsEditOpen(true)
  }

  function handleEditOpenChange(open: boolean) {
    if (open) openEditor()
    else setIsEditOpen(false)
  }

  async function handleSave(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!isValid) return
    setFieldError(undefined)
    setGeneralError(undefined)

    // Nur die tatsächlich geänderten Felder (K3,
    // `docs/work/004a-sollstellung-cdc84c/findings.md`): Ein `PATCH`, der
    // unveränderte Konditionsfelder oder `tenant_id` mitschickt, lief in
    // `terms_locked`/`tenant_locked`, sobald der Vertrag eine Sollstellung
    // trägt — auch wenn nur ein unabhängiges Feld (z. B. die Kaution) geändert
    // werden sollte.
    const body = buildContractPatchBody(form, initial)

    try {
      await updateMutation.mutateAsync({ contractId: entityId, ifMatch: String(contract.version), body })
      setIsEditOpen(false)
      setConfirmation('Änderungen gespeichert')
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleUnarchive() {
    setArchiveError(undefined)
    try {
      await unarchiveMutation.mutateAsync({ contractId: entityId, ifMatch: String(contract.version) })
    } catch (error) {
      setArchiveError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  return (
    // Fragment statt direkt im `<form>`: `ContractTermsSheet` trägt selbst ein
    // eigenes `<form>` (Enter-zum-Speichern) und wird über `Sheet`/
    // `SheetPortal` an `document.body` gehängt — im DOM also **kein**
    // Nachfahre dieses Formulars. Im **React-Baum** ist es das aber
    // weiterhin, und React lässt Ereignisse eines Portals genau diesem Baum
    // folgen (dokumentiertes Verhalten, nicht dem DOM-Baum): Lag das Sheet
    // innerhalb dieses `<form>`, blubberte sein `submit` bis zu `handleSave`
    // hier hoch und schickte zusätzlich ein `PATCH` mit den (gesperrten)
    // Stammdaten-Feldern ab — sichtbar als 409 `terms_locked` auf
    // `contract-edit-error-base_rent_net_cents`, obwohl „Konditionen ändern"
    // gerade erfolgreich war. Gefunden bei den Aufnahmen zu Spec 04a.
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-4">
          <dl data-testid="contract-master-data-read" className="grid gap-3 sm:grid-cols-2">
            <ContractReadField label="Objekt" value={contract.subject.label} testId="contract-read-subject" />
            <ContractReadField label="Mieter" value={contract.tenant.label} testId="contract-read-tenant" />
            <ContractReadField label="Status" value={contract.status.label} testId="contract-read-status" />
            <ContractReadField label="Zeitraum" value={contract.period_display} testId="contract-read-period" />
            <ContractReadField label="Beginn" value={contract.starts_on} testId="contract-read-starts-on" />
            <ContractReadField label="Kündigungsfrist (Monate)" value={contract.notice_period_months} testId="contract-read-notice-period-months" />
            <ContractReadField label="Befristet bis" value={contract.ends_on} testId="contract-read-ends-on" />
            <ContractReadField label="Gekündigt zum" value={contract.terminated_on} testId="contract-read-terminated-on" />
            <ContractReadField label="Vertragsnummer" value={contract.contract_ref} testId="contract-read-contract-ref" />
          </dl>
          <ContractTermsSummary contract={contract} />
        </div>
        {canEdit && !isArchived && (
          <button type="button" ref={editTriggerRef} data-testid="contract-edit-open" onClick={openEditor} className="border-border text-text border px-3">
            Bearbeiten
          </button>
        )}
      </div>
      {isArchived ? (
        <ArchivedBanner
          kind="contract"
          canUnarchive={canUnarchive}
          parentId={contract.subject.id}
          archivedById={archivedById}
          archivedByLabel={archivedByLabel}
          onUnarchive={() => void handleUnarchive()}
          isUnarchiving={unarchiveMutation.isPending}
        />
      ) : (
        <ArchiveControls
          kind="contract"
          entityId={entityId}
          label={contract.label}
          version={contract.version}
          parentId={contract.subject.id}
          isArchiving={archiveMutation.isPending}
          onArchive={(ifMatch) => archiveMutation.mutateAsync({ contractId: entityId, ifMatch })}
          onError={setArchiveError}
          archivePreview={archivePreviewMutation}
        />
      )}
      {archiveError !== undefined && <p data-testid="contract-archive-error" role="alert" className="text-label text-status-overdue">{archiveError}</p>}
      <MasterDataEditSheet open={isEditOpen} onOpenChange={handleEditOpenChange} title="Vertrag bearbeiten"
        triggerRef={editTriggerRef}
        formId="contract-edit-form"
        saveTestId="contract-edit-save"
        saveDisabled={!isDirty || !isValid || updateMutation.isPending}
        isSaving={updateMutation.isPending}
      >
      <form id="contract-edit-form" onSubmit={(event) => void handleSave(event)} className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
        <div className={`text-label text-muted flex flex-col gap-1 ${FIELD_CLASS} border-dashed`}>
          <Link
            to="/objekt/$entityId"
            params={{ entityId: contract.subject.id }}
            search={{ tab: undefined }}
            data-testid="contract-subject"
            className="text-accent hover:underline"
          >
            {contract.subject.label}
          </Link>
          <span data-testid="contract-status">{contract.status.label}</span>
          <span>{contract.period_display}</span>
        </div>

        <TenantPicker
          testId="contract-edit-tenant"
          value={{ id: form.tenantId, label: form.tenantLabel, salutation: tenantSalutation }}
          onSelect={selectTenant}
          linkToTenant
          error={fieldError}
          errorPrefix="contract-edit"
          disabled={isArchived}
        />

        <FormField
          label="Beginn"
          testId="contract-edit-starts-on"
          apiField="starts_on"
          errorPrefix="contract-edit"
          type="date"
          required
          disabled={isArchived}
          value={form.startsOn}
          onChange={updateField('startsOn')}
          error={fieldError}
        />
        <FormField
          label="Kaltmiete"
          testId="contract-edit-base-rent"
          apiField="base_rent_net_cents"
          errorPrefix="contract-edit"
          inputMode="decimal"
          disabled={isArchived || hasCharges}
          value={form.baseRent}
          onChange={updateField('baseRent')}
          error={fieldError}
        />
        <FormField
          label="Nebenkostenvorauszahlung"
          testId="contract-edit-operating-cost-advance"
          apiField="operating_cost_advance_net_cents"
          errorPrefix="contract-edit"
          inputMode="decimal"
          disabled={isArchived || hasCharges}
          value={form.operatingCostAdvance}
          onChange={updateField('operatingCostAdvance')}
          error={fieldError}
        />
        {/* Sobald eine Sollstellung existiert, ersetzt der Konditionswechsel
         * das direkte Bearbeiten von Miete und Nebenkosten (Spec 04a,
         * „Konditionen wechseln zum Monatsersten" — AK-294). */}
        {!isArchived && hasCharges && (
          <button
            type="button"
            data-testid="contract-terms-add"
            onClick={() => {
              setIsTermsSheetOpen(true)
            }}
            className={`text-text self-start px-4 ${FIELD_CLASS}`}
          >
            Konditionen ändern
          </button>
        )}
        <FormField
          label="Fälligkeitstag"
          testId="contract-edit-payment-due-day"
          apiField="payment_due_day"
          errorPrefix="contract-edit"
          inputMode="numeric"
          disabled={isArchived}
          value={form.paymentDueDay}
          onChange={updateField('paymentDueDay')}
          error={fieldError}
        />
        <div className="flex flex-col gap-1">
          <FormField
            label="Kaution"
            testId="contract-edit-deposit"
            apiField="deposit_cents"
            errorPrefix="contract-edit"
            inputMode="decimal"
            disabled={isArchived}
            value={form.deposit}
            onChange={updateField('deposit')}
            error={fieldError}
          />
          {/* `ARCHITECTURE.md` 4.5 verlangt genau diese Trennung — ohne den
           * Satz erwartet jeder Nutzer das Gegenteil (Spec 03). */}
          <p className="text-label text-muted">Eine Kautionsforderung entsteht daraus nicht von selbst.</p>
        </div>
        <FormField
          label="Kündigungsfrist (Monate)"
          testId="contract-edit-notice-period-months"
          apiField="notice_period_months"
          errorPrefix="contract-edit"
          inputMode="numeric"
          disabled={isArchived}
          value={form.noticePeriodMonths}
          onChange={updateField('noticePeriodMonths')}
          error={fieldError}
        />
        <FormField
          label="Befristet bis"
          testId="contract-edit-ends-on"
          apiField="ends_on"
          errorPrefix="contract-edit"
          type="date"
          disabled={isArchived}
          value={form.endsOn}
          onChange={updateField('endsOn')}
          error={fieldError}
        />
        <FormField
          label="Gekündigt zum"
          testId="contract-edit-terminated-on"
          apiField="terminated_on"
          errorPrefix="contract-edit"
          type="date"
          disabled={isArchived}
          value={form.terminatedOn}
          onChange={updateField('terminatedOn')}
          error={fieldError}
        />
        <FormField
          label="Vertragsnummer"
          testId="contract-edit-contract-ref"
          apiField="contract_ref"
          errorPrefix="contract-edit"
          disabled={isArchived}
          value={form.contractRef}
          onChange={updateField('contractRef')}
          error={fieldError}
        />

        {generalError !== undefined && (
          <p data-testid="contract-edit-error" role="alert" className="text-label text-status-overdue">
            {generalError}
          </p>
        )}

      </form>
      </MasterDataEditSheet>
      <ConfirmationStrip message={confirmation} onDismiss={() => { setConfirmation(null) }} testId="object-save-confirmation" />

      <ContractTermsSheet
        open={isTermsSheetOpen}
        onOpenChange={setIsTermsSheetOpen}
        contractId={entityId}
        terms={contract.terms}
      />
    </>
  )
}
